<template>
  <delete-all-products-modal
    v-if="deleteAllProductModal"
    @closeModal="toggleDeleteAllProductsModal"
    @deleteAllProducts="handleRemoveAllProductFromCart"
  />
  <delete-order-modal
    v-if="deleteOrderModal"
    @closeModal="toggleDeleteOrderModal"
    @delete-order="handleDeleteOrder"
  />
  <section
    :class="hasSaleTabs ? 'xl:h-[calc(100vh-176px)]' : 'xl:h-[calc(100vh-128px)]'"
    class="relative xl:w-[300px] xl:flex-none z-40"
  >
    <div
      :class="[
        'xl:fixed xl:left-0 z-[35]',
        hasSaleTabs ? 'xl:h-[calc(100vh-176px)] has-sale-tabs' : 'xl:h-[calc(100vh-128px)]',
        cartOpen ? 'xl:w-[80%]' : 'xl:w-[300px]',
      ]"
      id="cart"
    >
      <!-- cart open btn -->
      <button
        aria-label="Toggle cart"
        class="hidden absolute h-[34px] w-[22px] xl:flex justify-center items-center bg-white right-[-21px] top-[33px] xl:border rounded-tr rounded-br border-grays-400 !border-l-0 overflow-hidden z-30"
        @click="toggleCart"
      >
        <span class="material-symbols-rounded text-primary-700 text-3xl">
          {{ cartOpen ? "chevron_left" : "chevron_right" }}
        </span>
      </button>

      <!-- cart content -->
      <div
        class="relative w-full h-screen lg:h-full flex flex-col justify-between bg-white xl:border border-l-0 rounded-tr rounded-br border-grays-400 xl:shadow-soft z-10"
      >
        <tooltip-user
          v-if="tooltipCart"
          :class="{
            'right-[25px]': cartOpen,
            'left-[325px]': !cartOpen,
          }"
          :direction="cartOpen ? 'right' : 'left'"
          class="absolute z-[60] top-[22px] md:block hidden"
          type="Cart"
          @close="closeTooltipCart"
        />
        <!-- intro -->
        <div
          :class="[
            'flex flex-none flex-col gap-[4px] px-[16px] xl:pt-[24px] pb-[8px] xl:pb-[24px] border-b border-grays-400',
            cartOpen && 'mx-[119px] px-[0]',
          ]"
          id="cart-title"
        >
          <h1 class="text-grays-900 text-xl font-bold">סל הקניות המשפחתי</h1>
          <p class="text-grays-800 text-sm font-regular hide-on-short-screen">
            כל מה שכאן, הוא שלכם. אפשר להוסיף, להתחרט, לשנות ולעדכן כמויות עד
            לזמן של סגירת המכירה להזמנות.
          </p>
        </div>

        <!-- cart products -->
        <cart-products-list
          v-if="cartProducts.length"
          :cartOpen="cartOpen"
          :cartProducts="cartProducts"
        />

        <!-- no cart items -->
        <div v-else class="flex flex-1 flex-col justify-center items-center">
          <h3 class="text-grays-600 text-base font-medium text-center">
            עוד לא בחרתם שום דבר. מחכים לכם על המדפים!
          </h3>
        </div>

        <!-- delete all button - mobile only. Sits above the summary frame so
             that frame stays attached to the payment area below it. -->
        <div
          class="flex xl:hidden flex-row justify-between pt-[10px] pb-[10px] px-5 hide-on-short-screen"
        >
          <button-text
            class="flex items-center gap-1 cursor-pointer justify-end whitespace-nowrap"
            @click="toggleDeleteAllProductsModal"
          >
            למחוק את כל המוצרים
            <span class="material-symbols-rounded text-lg">delete</span>
          </button-text>
          <button-text
            class="flex items-center gap-1 cursor-pointer justify-end !text-error border !border-error whitespace-nowrap"
            @click="toggleDeleteOrderModal"
          >
            מחק הזמנה
          </button-text>
        </div>

        <!-- payment summary - the narrow cart on every screen size.
             Holds the total, the product count and the details expander. -->
        <div
          v-if="!cartOpen"
          :class="
            !paymentDetailsOpen && !underHayadataThreshold ? 'rounded-br' : ''
          "
          class="flex flex-none flex-col bg-gray_semantic border-y border-grays-400 shadow-interface px-[14px] py-[16px]"
        >
          <div class="flex flex-row items-center justify-between">
            <span class="text-grays-800 text-base font-regular">
              {{ totalCartProductsAmount }} מוצרים
            </span>
            <p class="text-primary-700 text-base font-bold">
              סך הכל: ₪{{ getTotal }}
            </p>
          </div>
          <div
            v-if="donationTotal > 0"
            class="mt-1 flex flex-col text-xs text-grays-700"
          >
            <span>מוצרי המכירה: ₪{{ regularTotal }}</span>
            <span>תרומה לקופה: ₪{{ donationTotal }}</span>
          </div>
          <button
            class="mt-[4px] h-[20px] self-end flex flex-row items-center text-grays-700"
            @click="paymentDetailsOpen = !paymentDetailsOpen"
          >
            <span class="text-sm font-regular leading-none">{{
              paymentDetailsOpen ? "להסתרת הפרטים" : "לפרטים נוספים"
            }}</span>
            <!-- the glyph sits inset inside its 20px box, so it is nudged out
                 to line the arrow up with the total above it -->
            <span
              class="material-symbols-rounded text-[20px] w-[20px] h-[20px] ml-[-3px] leading-none overflow-hidden"
              >{{ paymentDetailsOpen ? "expand_less" : "expand_more" }}</span
            >
          </button>
        </div>

        <!-- number of products - wide cart keeps its own row -->
        <div
          v-if="cartOpen"
          class="hidden xl:flex flex-none flex-row items-center justify-between py-[16px] border-t border-grays-400 mx-[119px]"
        >
          <div
            class="text-grays-900 text-sm flex-row items-center justify-between w-full"
          >
            <span>סה”כ {{ totalCartProductsAmount }} מוצרים</span>
          </div>
          <div class="flex flex-row gap-6">
            <button-text
              v-if="cartOpen && cartProducts.length"
              class="flex items-center gap-1 cursor-pointer pl-0 whitespace-nowrap"
              @click="toggleDeleteAllProductsModal"
            >
              למחוק את כל המוצרים
              <span class="material-symbols-rounded text-lg">delete</span>
            </button-text>
            <button-tertiary
              v-if="cartOpen"
              class="!text-error !border !border-error whitespace-nowrap"
              @click="toggleDeleteOrderModal"
              >מחק הזמנה
            </button-tertiary>
          </div>
        </div>
        <!-- payment. On the narrow desktop cart this frame only appears when
             the details are expanded, or on its own to carry the hint button.
             Mobile and the wide cart keep it visible as before. -->
        <div
          :class="[
            'flex flex-col justify-between bg-gray_semantic p-[14px] pt-[9px] rounded-b',
            !cartOpen ? 'pt-[14px]' : '',
            cartOpen && 'pt-[27px] pb-[31px] pl-[65px] pr-[56px] gap-[18px]',
            !cartOpen && !paymentDetailsOpen && !underHayadataThreshold
              ? 'hidden'
              : '',
            !cartOpen ? 'items-center justify-start' : '',
          ]"
        >
          <!-- prepaid form -->
          <div class="flex justify-between flex-col w-full">
            <div v-show="!cartOpen" :class="paymentDetailsOpen ? '' : 'hidden'">
              <!-- the two selects, shown directly behind the details expander -->
              <div>
                <form
                  class="flex flex-row justify-between gap-[12px]"
                  @submit.prevent
                >
                  <div class="flex flex-col gap-[4px]">
                    <label class="text-grays-700 text-sm font-regular"
                      >תשלום באמצעות</label
                    >
                    <select-payment
                      :credit-cards="creditCards"
                      :current-card="currentCard"
                      :openUp="true"
                      @card-changed="handleChangeCard"
                    />
                  </div>
                  <div v-if="maxPayment > 1" class="flex flex-col gap-[4px]">
                    <label class="text-grays-700 text-sm font-regular"
                      >תשלומים</label
                    >
                    <select-number
                      :current-payments="currentPayments"
                      :max-number="maxPayment"
                      @payment-changed="handleChangePayment"
                    />
                  </div>
                </form>
              </div>
            </div>

            <div
              v-if="cartOpen"
              class="flex flex-row justify-between gap-[4px]"
            >
              <div class="flex flex-row items-center gap-6">
                <div class="text-sm text-grays-700">תשלום באמצעות</div>
                <div class="flex-row flex gap-2">
                  <img
                    alt="Cardol"
                    class="h-3"
                    src="@/assets/icons/logos/Cardcol.png"
                  />
                  <img
                    alt=""
                    class="h-3"
                    src="@/assets/icons/logos/nedarim.svg"
                  />
                </div>
                <div class="h-full bg-grays-300 w-[1px]"></div>
                <form
                  class="flex flex-row gap-[12px] items-center"
                  @submit.prevent
                >
                  <div class="text-sm text-grays-700">כרטיס אשראי חילופי:</div>
                  <div class="flex flex-col gap-[4px]">
                    <label class="text-grays-700 text-sm font-regular"
                      >תשלומים</label
                    >
                    <select-payment
                      :credit-cards="creditCards"
                      :current-card="currentCard"
                      :openUp="true"
                      @card-changed="handleChangeCard"
                    />
                  </div>
                  <div v-if="maxPayment > 1" class="flex flex-col gap-[4px]">
                    <label class="text-grays-700 text-sm font-regular"
                      >תשלומים</label
                    >
                    <select-number
                      :current-payments="currentPayments"
                      :max-number="maxPayment"
                      @payment-changed="handleChangePayment"
                    />
                  </div>
                </form>
              </div>

              <div class="flex flex-row gap-1 items-end">
                <span class="material-symbols-rounded text-primary-700 text-xl"
                  >payments</span
                >
                <p class="text-primary-700 text-lg font-bold">
                  סך הכל: ₪{{ getTotal }}
                </p>
              </div>
              <div
                v-if="donationTotal > 0"
                class="text-sm text-grays-700 text-left"
              >
                מוצרי המכירה: ₪{{ regularTotal }} · תרומה לקופה: ₪{{ donationTotal }}
              </div>
            </div>
          </div>

          <!-- the payment frame centres its children, so this row has to claim
               the full width before the text inside it can align right -->
          <div
            :class="[
              'mt-1 md:mt-5 flex justify-between items-center mb-[3px] md:mb-[16px]',
              !cartOpen ? 'mt-[14px] mb-0 w-full' : '',
              !cartOpen && !paymentDetailsOpen ? 'hidden' : '',
            ]"
            id="cart-footer"
          >
            <div
              :class="[cartOpen ? '!flex-row gap-[7px]' : '', 'flex flex-col']"
            >
              <p
                :class="[
                  'text-grays-900 text-sm font-medium',
                  !cartOpen
                    ? 'text-grays-800 font-regular leading-[135%] text-right'
                    : '',
                ]"
              >
                התשלום ייגבה אוטומטית קרוב לתאריך איסוף המכירה.
                <span class="font-bold hide-on-short-screen"
                  >שווה שבחרתם לחסוך במשנת יוסף!
                </span>
              </p>
            </div>
            <div
              v-if="cartOpen && underHayadataThreshold"
              class="flex flex-row justify-start"
            >
              <button-tooltip-hayadata
                :openUp="true"
                :tooltip-hayadata-texts="{
                  title_bold: 'איפה כפתור התשלום?',
                  description:
                    'ההזמנה שלכם גמישה לשינויים ועריכה עד שהמכירה נסגרת. אנחנו רוצים להיות בטוחים שכל ההזמנה תגיע אליכם ולכן כל מוצר שתכניסו או תערכו בהזמנה נשמר בשבילכם. בלי שתצטרכו לזכור לאשר בסוף את ההזמנה כדי לקבל אותה. התשלום מתבצע בצורה אוטומטית סמוך לאספקה. וזה גם עוזר לנו להמנע מגביה מיותרת על מוצרים שלא סופקו.',
                }"
              />
            </div>
          </div>

          <!-- the total lives in the summary frame above now, on every size -->

          <div
            v-if="!cartOpen && underHayadataThreshold"
            :class="[
              'flex flex-row justify-start',
              paymentDetailsOpen ? 'mt-[14px]' : '',
            ]"
          >
            <button-tooltip-hayadata
              :openUp="true"
              :tooltip-hayadata-texts="{
                title_bold: 'איפה כפתור התשלום?',
                description:
                  'ההזמנה שלכם גמישה לשינויים ועריכה עד שהמכירה נסגרת. אנחנו רוצים להיות בטוחים שכל ההזמנה תגיע אליכם ולכן כל מוצר שתכניסו או תערכו בהזמנה נשמר בשבילכם. בלי שתצטרכו לזכור לאשר בסוף את ההזמנה כדי לקבל אותה. התשלום מתבצע בצורה אוטומטית סמוך לאספקה. וזה גם עוזר לנו להמנע מגביה מיותרת על מוצרים שלא סופקו.',
              }"
              class="!w-full"
            />
          </div>
        </div>
      </div>
    </div>
    <tooltip-user
      v-if="tooltipGreen"
      :class="{
        'bottom-[110px] !left-[55px]': cartOpen,
      }"
      class="!h-fit self-end absolute z-[60] left-[15px] bottom-[70px] md:block hidden"
      direction="bottom"
      type="GreenTooltip"
      @close="closeTooltipGreen"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { cartHayadataTexts } from "@/data/Texts";
import axiosInstance from "../../helpers/axios.js";
import ButtonText from "@/components/Global/ButtonText.vue";
import CartProductsList from "@/components/Order/CartProductsList.vue";
import SelectPayment from "@/components/Global/SelectPayment.vue";
import SelectNumber from "@/components/Global/SelectNumber.vue";
import ButtonTooltipHayadata from "@/components/Global/ButtonTooltipHayadata.vue";
import DeleteAllProductsModal from "@/components/Order/DeleteAllProductsModal.vue";
import ButtonTertiary from "@/components/Global/ButtonTertiary.vue";
import DeleteOrderModal from "@/components/Order/DeleteOrderModal.vue";
import TooltipUser from "@/components/Tooltip/TooltipUser.vue";
import TooltipTashloumim from "../Global/TooltipTashloumim.vue";
import CardBrandIcon from "@/components/Global/CardBrandIcon.vue";

export default {
  name: "Cart",
  components: {
    TooltipUser,
    DeleteOrderModal,
    ButtonTertiary,
    DeleteAllProductsModal,
    ButtonTooltipHayadata,
    SelectNumber,
    SelectPayment,
    CartProductsList,
    ButtonText,
    TooltipTashloumim,
    CardBrandIcon,
  },
  data() {
    return {
      cartOpen: false,
      cartHayadataTexts,
      creditCards: [],
      // "לפרטים נוספים" expander under the total, narrow desktop cart only
      paymentDetailsOpen: false,
      currentPayments: this.currentPaymentsProp || null,
      currentCard: {},
      deleteAllProductModal: false,
      deleteOrderModal: false,
      tooltipGreen: false,
      tooltipCart: false,
    };
  },
  props: {
    orderId: {
      type: String,
      required: true,
    },
    currentPaymentsProp: {
      type: Number,
    },
    currentCardProp: {
      type: Object,
    },
    maxPayment: {
      type: Number,
      default: null,
    },
    // The sale tabs strip pushes the page down by its own height, so the
    // cart has that much less viewport to fill.
    hasSaleTabs: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    ...mapActions({
      showToast: "orderToast/showToast",
    }),
    closeTooltipCart() {
      this.tooltipCart = false;
    },
    closeTooltipGreen() {
      this.tooltipGreen = false;
    },
    async handleChangePayment(paymentsNr) {
      this.currentPayments = paymentsNr;
      await axiosInstance
        .put("/credit_card_order/" + this.orderId, {
          tas: this.currentPayments,
          card_id: this.currentCard.id,
        })
        .then((res) => {
          this.showToast({
            message: res.data.message,
            type: res.data.status,
          });
        });
    },
    async handleChangeCard(card) {
      if (this.currentCard.save_card !== card.save_card) {
        this.currentCard = card;
        await axiosInstance
          .put("/credit_card_order/" + this.orderId, {
            card_id: card.id,
            tas: this.currentPayments,
          })
          .then((res) => {
            this.showToast({
              message: res.data.message,
              type: res.data.status,
            });
          })
          .catch((error) => {
            this.showToast({
              message: error.response.data.message,
              type: "error",
            });
          });
      }
    },
    toggleCart() {
      this.cartOpen = !this.cartOpen;
    },
    ...mapMutations({
      removeAllFromCart: "cart/removeAllFromCart",
    }),
    toggleDeleteOrderModal() {
      this.deleteOrderModal = !this.deleteOrderModal;
    },
    toggleDeleteAllProductsModal() {
      this.deleteAllProductModal = !this.deleteAllProductModal;
    },
    async handleDeleteOrder() {
      try {
        await axiosInstance.post(
          `/orders/${this.$route.params.orderID}/delete_order`
        );
        this.toggleDeleteOrderModal();
        this.$router.push("/sales-board");
      } catch (e) {
        console.error(e);
      }
    },
    async handleRemoveAllProductFromCart() {
      try {
        await axiosInstance.get(
          `/orders/${this.$route.params.orderID}/delete_all_items`
        );
        this.removeAllFromCart();
        this.showToast({
          message: "כל הפריטים נמחקו בהצלחה",
          type: "success",
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.toggleDeleteAllProductsModal();
      }
    },
    async getCards() {
      const response = await axiosInstance.get("/payment_methods/cards");
      this.creditCards = response.data.filter((card) => card.type === "cc");
    },
    async getTooltipInfo() {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (Object.keys(user).length === 0) {
        return;
      }
      const tooltips = user.tooltips;
      const tooltipCart = tooltips.find((item) => item.name === "Cart");
      const tooltipGreen = tooltips.find(
        (item) => item.name === "Green tooltip"
      );

      if (!tooltipCart || (tooltipCart && !tooltipCart.finished)) {
        if (tooltipCart === undefined) {
          return;
        }
        setTimeout(() => {
          this.tooltipCart = true;
          axiosInstance
            .post(`/profile/finish_tooltip/${tooltipCart.id}`)
            .then(() => {
              const latestData = JSON.parse(localStorage.getItem("user"));
              const latestTooltips = latestData.tooltips;
              const latestTooltipSalesBoard = latestTooltips.find(
                (item) => item.name === "Cart"
              );
              latestTooltipSalesBoard.finished = true;
              localStorage.setItem("user", JSON.stringify(latestData));
              setTimeout(() => {
                this.tooltipCart = false;
              }, 1000 * 30);
            });
        }, 1000 * 30);
      }

      if (!tooltipGreen || (tooltipGreen && !tooltipGreen.finished)) {
        if (tooltipGreen === undefined) {
          return;
        }
        setTimeout(() => {
          this.tooltipGreen = true;
          axiosInstance
            .post(`/profile/finish_tooltip/${tooltipGreen.id}`)
            .then(() => {
              const latestData = JSON.parse(localStorage.getItem("user"));
              const latestTooltips = latestData.tooltips;
              const latestTooltipSalesBoard = latestTooltips.find(
                (item) => item.name === "Green tooltip"
              );
              latestTooltipSalesBoard.finished = true;
              localStorage.setItem("user", JSON.stringify(latestData));
              setTimeout(() => {
                this.tooltipGreen = false;
              }, 1000 * 30);
            });
        }, 1000 * 30);
      }
    },
  },
  computed: {
    ...mapState({
      cartProducts: (state) => state.cart.cart,
    }),
    ...mapGetters({
      getTotal: "cart/getTotal",
    }),
    ...mapGetters("user", ["getTotalLoginCartCount", "getOrdersCount"]),
    totalCartProductsAmount() {
      return this.cartProducts.reduce(
        (acc, product) => acc + (product.amount || 0),
        0
      );
    },
    donationTotal() {
      return this.cartProducts
        .filter((product) => product.organization_item)
        .reduce((total, product) => total + product.price1 * product.amount, 0)
        .toFixed(2);
    },
    regularTotal() {
      return this.cartProducts
        .filter((product) => !product.organization_item)
        .reduce((total, product) => total + product.price1 * product.amount, 0)
        .toFixed(2);
    },
    lastCardNumber() {
      return this.currentCard.save_card.slice(-4) || "****";
    },
    // The "why is there no pay button" hint stops once the customer has
    // actually completed three orders - not three carts, three orders.
    underHayadataThreshold() {
      return this.getOrdersCount < 3;
    },
  },
  watch: {
    currentCardProp: {
      immediate: true,
      handler(newVal) {
        this.currentCard = JSON.parse(JSON.stringify(newVal));
      },
      deep: true,
    },
  },

  created() {
    this.currentCard = JSON.parse(JSON.stringify(this.currentCardProp));
  },
  mounted() {
    this.getCards().then(() => {
      // After fetching cards, find the matching card to get the full card data (including brand)
      if (this.currentCard?.save_card && this.creditCards.length) {
        const matchingCard = this.creditCards.find(
          (card) => card.save_card === this.currentCard.save_card
        );
        if (matchingCard) {
          this.currentCard = { ...matchingCard };
        }
      }
    });
    this.getTooltipInfo();
  },
};
</script>

<style scoped></style>
